/**
 * IntegrationDataMapping — shared field mapping page container.
 *
 * Handles:
 *   - Expandable field mapping guide (passed as optional `guide` render prop)
 *   - Loading existing panels from the API (one per scope that has mappings)
 *   - "Add Mapping" button to create new panels
 *   - "All changes saved" indicator
 *
 * Integrations that previously had near-identical DataMapping containers
 * (Shopify, Accela, and future ones) use this component instead.
 */

import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import SearchBar from "@/components/SearchBar";
import { IconPlus, IconCheck } from "@velaro/velaro-component-lib/";
import { PrimaryButton } from "@/components/ds";

import IntegrationMappingPanel, {
  IntegrationMappingPanelProps
} from "./IntegrationMappingPanel";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** The config ID for this integration instance. */
  selectedConfigId: number;
  teamId: number;
  /** Description line shown above the guide toggle button. */
  description: string;
  /** Render prop for the integration-specific guide content. */
  guide?: React.ReactNode;
  /** Label for the guide toggle button, e.g. "Field Mapping Guide ▾". */
  guideLabel?: string;

  // -- forwarded to IntegrationMappingPanel --
  fieldKey: string;
  fieldLabel: string;
  apiPrefix: string;
  getFieldsPath: string;
  /** Full list of scope strings — used to query for existing panels on load. */
  scopeOptions: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IntegrationDataMapping(props: Props) {
  const {
    selectedConfigId,
    teamId,
    description,
    guide,
    guideLabel = "Field Mapping Guide ▾",
    fieldKey,
    fieldLabel,
    apiPrefix,
    getFieldsPath,
    scopeOptions
  } = props;

  const api = useApi();

  const [panels, setPanels] = useState<{ id: number; scopeType: string }[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  // Default open — the guide explains what field mapping does before the
  // admin starts clicking checkboxes; collapsing it by default hid the one
  // place that explained the terminology up front.
  const [showGuide, setShowGuide] = useState(true);
  const [filterText, setFilterText] = useState("");

  // Defaults to the first scope option not already open in another panel — never "".
  // CustomSelect renders a plain native <select> whose bound `value` must match one of
  // its <option> elements; a "" value that isn't itself an option leaves the browser
  // showing the first <option> visually (native select fallback) while React's state
  // stays "". Every downstream effect in IntegrationMappingPanel — fetchMetadata,
  // fetchMapping, and the "Add field"/"Delete record" buttons — is gated on
  // `if (!scopeType) return` / `{scopeType && ...}`, so a blank-started panel looked
  // selected but was silently inert until the admin manually reselected the dropdown.
  const addPanel = () => {
    const usedScopes = new Set(panels.map((p) => p.scopeType));
    const nextScope =
      scopeOptions.find((s) => !usedScopes.has(s)) ?? scopeOptions[0] ?? "";
    setPanels((prev) => [
      ...prev,
      { id: prev.length + 1, scopeType: nextScope }
    ]);
  };

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const fetchMappingFieldCount = useCallback(
    async (scopeType: string) => {
      if (!scopeType || selectedConfigId === 0) return { scopeType, count: 0 };
      try {
        const resp = await api.messaging.get(
          `${apiPrefix}/Mapping/${scopeType}/${selectedConfigId}`
        );
        if (!resp.ok) return { scopeType, count: 0 };
        const json = await resp.json();
        return { scopeType, count: Array.isArray(json) ? json.length : 0 };
      } catch {
        return { scopeType, count: 0 };
      }
    },
    [api.messaging, apiPrefix, selectedConfigId]
  );

  const fetchAllMappings = useCallback(async () => {
    if (!selectedConfigId) return;
    setPanels([]);
    setLoading(true);
    try {
      const results = await Promise.all(
        scopeOptions.filter(Boolean).map(fetchMappingFieldCount)
      );
      const activePanels = results
        .filter(({ count }) => count > 0)
        .map(({ scopeType }, i) => ({ id: i + 1, scopeType }));
      setPanels(activePanels);
    } catch (err) {
      console.error(`${fieldLabel}: failed to load panels`, err);
    } finally {
      setLoading(false);
    }
  }, [selectedConfigId, scopeOptions, fetchMappingFieldCount, fieldLabel]);

  useEffect(() => {
    if (selectedConfigId > 0) fetchAllMappings();
  }, [selectedConfigId, fetchAllMappings]);

  // Show "saved" briefly on first load to confirm connection
  useEffect(() => {
    showSavedMessage();
  }, []);

  // Shared props forwarded to every panel
  const panelConfig: Omit<IntegrationMappingPanelProps, "scopeType"> = {
    selectedConfigId,
    teamId,
    showSavedMessage,
    isDeleteDisabled: panels.length === 1,
    fieldKey,
    fieldLabel,
    apiPrefix,
    getFieldsPath,
    scopeOptions,
    filterText,
    onClearFilter: () => setFilterText("")
  };

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-500">{description}</p>
        {guide && (
          <button
            type="button"
            onClick={() => setShowGuide((s) => !s)}
            aria-expanded={showGuide}
            className="shrink-0 px-3 py-1.5 text-xs font-medium text-primary bg-primary-bg rounded-lg hover:bg-primary-bg transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            {showGuide ? "Hide Guide" : guideLabel}
          </button>
        )}
      </div>

      {showGuide && guide && <div className="mb-4">{guide}</div>}

      <div className="justify-between items-center inline-flex w-full">
        <div className="justify-start items-start flex">
          <SearchBar
            placeholderText="Search mapped fields"
            onSearch={setFilterText}
          />
        </div>
        <div className="justify-end items-center gap-3 flex w-full">
          {showSaved && (
            <div className="flex items-center gap-2 text-zinc-500">
              <span className="filter invert">
                {React.createElement(IconCheck, { className: "text-zinc-500" })}
              </span>
              <div className="text-zinc-500 text-sm font-medium">
                All changes saved
              </div>
            </div>
          )}
          <PrimaryButton
            onClick={addPanel}
            className="font-medium h-full"
            prefixIcon={React.createElement(IconPlus)}
          >
            Add Mapping
          </PrimaryButton>
        </div>
      </div>

      {loading && (
        <div className="text-center my-4">
          <span>Loading mappings...</span>
        </div>
      )}

      <div className="mt-4">
        {panels.map((panel) => (
          <IntegrationMappingPanel
            key={panel.id}
            {...panelConfig}
            scopeType={panel.scopeType}
          />
        ))}
      </div>
    </div>
  );
}
